import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactSelect from 'react-select';
import { TriangleAlert, Loader2, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import PublicLayout from '../layouts/PublicLayout';
import {
  useOnboardingStatus,
  useSubmitSelfReport,
  fetchSaSignLink,
} from '../../services/onboarding';
// Fixed dark-grey brand theme shared with /register + /referral.
import { BUTTON, INPUT, LABEL, PAGE_BG, SELECT_DARK, useBrandBodyBackground } from '../referral/brandTheme';

// The veteran's onboarding home (client-intake overhaul): an HONEST status
// timeline instead of the eternal "less than 24 hours" spinner. Four stages:
// Account created -> Confirm your details -> Sign your agreement -> You're
// in. The pre-fill wizard stores PROPOSALS (userSelfReport) — our team
// confirms the real facts on a call before the agreement goes out.

const RATING_OPTIONS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// 'half': the veteran's part of the stage is done (green check) but ours
// isn't - the connector below fades green-to-grey so the timeline reads
// "in motion" without claiming the stage is complete.
const StageDot = ({ state }) => (
  <div
    className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
      state === 'done' || state === 'half'
        ? 'bg-emerald-500 text-white'
        : state === 'active'
          ? 'bg-white text-black'
          : 'bg-white/10 text-neutral-500'
    }`}
  >
    {state === 'done' || state === 'half' ? '✓' : '•'}
  </div>
);

const Stage = ({ state, title, children, last }) => (
  <div className="flex text-start">
    <div className="mr-3 flex flex-col items-center">
      <StageDot state={state} />
      {!last ? (
        <div
          className={`w-0.5 min-h-[18px] flex-1 ${
            state === 'done'
              ? 'bg-emerald-500'
              : state === 'half'
                ? 'bg-gradient-to-b from-emerald-500 to-white/10'
                : 'bg-white/10'
          }`}
        />
      ) : null}
    </div>
    <div className="flex-1 pb-6">
      <div className={`font-bold ${state === 'pending' ? 'text-neutral-500' : 'text-white'}`}>
        {title}
      </div>
      {children ? <div className="mt-1 text-sm text-neutral-300">{children}</div> : null}
    </div>
  </div>
);

// Spinner-less number input: the value is typed, not stepped.
const NUMBER_INPUT = `${INPUT} [color-scheme:dark] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

const RATING_SELECT_OPTIONS = RATING_OPTIONS.map((r) => ({
  value: r,
  label: r === 0 ? "I don't currently have a VA rating (0%)" : `${r}%`,
}));
const MARRIED_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const SelfReportWizard = ({ onSaved }) => {
  const [rating, setRating] = React.useState(null);
  const [married, setMarried] = React.useState(null);
  // Kept as TEXT so the field can be emptied while typing (Nic 2026-09-23:
  // a numeric state snapped an erased field straight back to 0, so on a
  // phone you ended up with "02"). Empty saves as 0.
  const [dep18, setDep18] = React.useState('0');
  const [depSchool, setDepSchool] = React.useState('0');
  const countInput = (raw) => {
    const digits = String(raw).replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    if (digits === '') return '';
    return String(Math.min(20, Number(digits)));
  };
  const submit = useSubmitSelfReport(onSaved);

  const canSave = rating !== null && married !== null;

  return (
    <div className="mt-2 space-y-3">
      <p className="mb-0">
        Answer a few quick questions so our team can prepare your call. We&apos;ll
        confirm everything with you before your agreement is created.
      </p>
      <div>
        <label className={LABEL}>Current VA disability rating</label>
        <ReactSelect
          styles={SELECT_DARK}
          options={RATING_SELECT_OPTIONS}
          value={rating}
          onChange={setRating}
          placeholder="Select…"
          isSearchable={false}
        />
      </div>
      <div>
        <label className={LABEL}>Are you married?</label>
        <ReactSelect
          styles={SELECT_DARK}
          options={MARRIED_OPTIONS}
          value={married}
          onChange={setMarried}
          placeholder="Select…"
          isSearchable={false}
        />
      </div>
      <div className="grid grid-cols-2 items-end gap-3">
        <div>
          <label className={LABEL}>Dependent children under 18</label>
          <input
            className={NUMBER_INPUT}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={dep18}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setDep18(countInput(e.target.value))}
          />
        </div>
        <div>
          <label className={LABEL}>Children 18-24 in school</label>
          <input
            className={NUMBER_INPUT}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={depSchool}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setDepSchool(countInput(e.target.value))}
          />
        </div>
      </div>
      <button
        type="button"
        className={`h-10 ${BUTTON}`}
        disabled={!canSave || submit.isLoading}
        onClick={() =>
          submit.mutate({
            rating: Number(rating.value),
            married: married.value === 'true',
            dependentsUnder18: Number(dep18) || 0,
            dependents18To24InSchool: Number(depSchool) || 0,
          })
        }
      >
        {submit.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Save my answers
      </button>
    </div>
  );
};

const Processing = () => {
  const { user, updateUser, signOut } = useUser();
  const navigate = useNavigate();
  const isPending = !!user && user.userType === 'pending_client';
  const { data: status, refetch, isError: statusFailed } = useOnboardingStatus({ enabled: isPending });
  const [signLoading, setSignLoading] = React.useState(false);

  useBrandBodyBackground();

  React.useEffect(() => {
    if (!user) {
      navigate('/sign-in', { replace: true });
      return;
    }
    if (user.userType !== 'pending_client') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    if (!status?.promoted) return;
    // Signed + promoted server-side - but the context user is a sign-in
    // snapshot that still says pending_client, and PrivateRoute bounces
    // that type off every page back here (the redirect loop Nic hit).
    // Patch the stored identity BEFORE navigating so the dashboard admits.
    // The claim id too (2026-09-28): the sign-in snapshot carried
    // _currentClaimId = null for a pending client, and the client dashboard
    // bounced a claimless user to /questionnaire.
    const finish = async () => {
      let claimId = null;
      try {
        const res = await axios.get('/claims/current');
        claimId = res.data?.claimId || null;
      } catch {
        // No claim yet: the dashboard's own fallback handles it.
      }
      if (user && user.userType === 'pending_client') {
        updateUser({
          userType: 'client',
          ...(claimId ? { _currentClaimId: claimId } : {}),
        });
      }
      navigate('/dashboard', { replace: true });
    };
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.promoted]);

  // Back from the signer (Nic 2026-10-24): the browser can restore this page
  // from its back-forward cache exactly as it left, still "Opening…"
  // (Safari on iPhone almost always does). A restored page gets the button
  // back and a fresh status, in case they signed before coming back.
  React.useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted) return;
      setSignLoading(false);
      refetch();
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSignLink = async () => {
    if (status?.saState === 'legacy_ready') {
      navigate('/processing/agreement');
      return;
    }
    setSignLoading(true);
    try {
      const res = await fetchSaSignLink();
      const body = res.body || res;
      if (body.signUrl) {
        // Keep "Opening…" up while the browser navigates - resetting here
        // flashes the button back to "Review & sign" mid-departure.
        window.location.assign(body.signUrl);
        return;
      }
      refetch();
      setSignLoading(false);
    } catch (_) {
      // Say so (Nic 2026-10-24): the button used to just reset silently.
      toast.error("We couldn't open your agreement. Please check your connection and try again.");
      refetch();
      setSignLoading(false);
    }
  };

  const shell = (content) => (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-xl py-6">
        <div className="relative rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          <button
            type="button"
            onClick={() => signOut(() => navigate('/sign-in'))}
            className="absolute right-4 top-4 inline-flex items-center gap-1 text-xs text-neutral-400 transition-colors hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
          {content}
        </div>
      </div>
    </PublicLayout>
  );

  // Promoted payload carries no stage fields - never render the timeline
  // from it (it would show a blank wizard for a beat before the redirect).
  if (!status || status.promoted) {
    return shell(
      status?.promoted ? (
        <p className="my-4 mb-0 text-center text-neutral-300">
          You&apos;re all set. Taking you to your dashboard…
        </p>
      ) : statusFailed && !status ? (
        // Says so instead of spinning forever (QA 2026-10-13).
        <div className="my-4 text-center text-neutral-300">
          <p className="mb-2">We couldn&apos;t load your status.</p>
          <button type="button" onClick={() => refetch()} className="text-sm font-semibold text-emerald-300 underline">
            Try again
          </button>
        </div>
      ) : (
        <div className="my-4 flex justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-neutral-300" aria-label="Loading status" />
        </div>
      ),
    );
  }

  const factsConfirmed = !!status.factsConfirmed;
  const selfReported = !!status.selfReport;
  const saReady = status.saState === 'ready' || status.saState === 'legacy_ready';
  const saSigned = status.saState === 'signed';
  const saUpdating = status.saState === 'updating';

  const confirmState = factsConfirmed ? 'done' : selfReported ? 'half' : 'active';
  const signState = saSigned ? 'done' : saReady || saUpdating ? 'active' : 'pending';

  return shell(
    <>
      {status.vaOutage ? (
        <div className="mb-5 flex items-start gap-3 rounded-xl bg-amber-400/10 p-4 text-start">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" aria-hidden />
          <div>
            <div className="text-sm font-bold text-amber-100">
              The VA is experiencing an outage
            </div>
            <div className="mt-0.5 text-[13px] text-amber-200/80">
              Onboarding may take a little longer than usual.
            </div>
          </div>
        </div>
      ) : null}
      <div className="mb-5 text-center">
        <h2 className="text-xl font-bold text-white">Thank you for your service</h2>
        <p className="mb-0 text-sm text-neutral-300">
          Here&apos;s exactly where your onboarding stands.
        </p>
      </div>

      <Stage state="done" title="Account created">
        You&apos;re registered and our team can see you.
      </Stage>

      <Stage state={confirmState} title="Confirm your details">
        {factsConfirmed ? (
          'Your details are confirmed.'
        ) : selfReported ? (
          <>
            Thanks, we have your answers.{' '}
            {status.claimedByFirstName
              ? `${status.claimedByFirstName} from our team will call to confirm them.`
              : 'Our team will call to confirm them.'}
          </>
        ) : (
          <SelfReportWizard onSaved={() => refetch()} />
        )}
      </Stage>

      <Stage state={signState} title="Sign your agreement">
        {saSigned ? (
          'Signed. Thank you!'
        ) : saUpdating ? (
          "We're updating your agreement. You'll be able to sign again shortly."
        ) : saReady ? (
          <div>
            <div className="mb-2">Your Service Agreement is ready.</div>
            <button
              type="button"
              className={`h-10 ${BUTTON}`}
              onClick={openSignLink}
              disabled={signLoading}
            >
              {signLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {signLoading ? 'Opening…' : 'Review & sign'}
            </button>
          </div>
        ) : (
          'Your agreement is prepared after our team confirms your details.'
        )}
      </Stage>

      <Stage state={saSigned ? 'active' : 'pending'} title="You're in" last>
        {saSigned
          ? 'Finalizing your account. This page will take you to your dashboard.'
          : 'Your client dashboard unlocks the moment your agreement is signed.'}
      </Stage>
    </>,
  );
};

export default Processing;
